/**
 * MKS-349 — mutations for the three accounting-import steps.
 *
 * All three are mutations, including the two reads: each posts a file, so
 * none of them is cacheable by a query key. Only `confirm` invalidates — the
 * other two write nothing.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  analyzeImportFile,
  confirmImportPairs,
  matchImportFile,
  type ColumnMapping,
  type ConfirmOptions,
  type ConfirmPair,
  type MatchOptions,
} from '../services/api/invoiceImport';
import { invalidateMany } from './queryUtils';

export function useAnalyzeImportFile() {
  return useMutation({
    mutationFn: (file: File) => analyzeImportFile(file),
  });
}

export function useMatchImportFile() {
  return useMutation({
    mutationFn: ({
      file,
      mapping,
      options,
    }: {
      file: File;
      mapping: ColumnMapping;
      options: MatchOptions;
    }) => matchImportFile(file, mapping, options),
  });
}

export function useConfirmImportPairs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pairs, options }: { pairs: ConfirmPair[]; options: ConfirmOptions }) =>
      confirmImportPairs(pairs, options),
    onSuccess: () => {
      // A confirmed back-fill changes invoice numbers, due dates and paid
      // status, so every surface derived from them is stale: the list and its
      // badges, a detail page left open in another tab, and the dashboard
      // receivables cards (`['dashboard', 'outstanding-invoices']` /
      // `['dashboard', 'receivables']`, both reached by the `['dashboard']`
      // prefix — invalidateQueries matches by prefix).
      invalidateMany(queryClient, [['invoices'], ['invoice'], ['dashboard']]);
    },
  });
}
